/**
 * React hook for checking flow type visibility
 *
 * Provides a convenient way to check flow type availability in React components.
 * Automatically handles GrowthBook integration, org features, and database limits.
 */

import { useMemo } from 'react';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import { FlowType } from 'new/elements/flows/types';
import {
  getFlowTypeVisibility,
  getFlowTypesVisibility,
  getConditionallyEnabledFlowTypes,
  parseOrgFeaturesEnabled,
  FlowTypeVisibilityResult,
  FlowTypeVisibilityConfig,
  GROWTHBOOK_ROLLOUT_FLOW_TYPES,
} from 'new/utils/flowTypeVisibility';
import { ORG_FEATURE_FLAG_SETTINGS } from 'js/constants/config';
import { FeatureFlagKey, isValidFeatureFlag } from 'new/constants/featureFlags';

/**
 * Hook to check if a specific flow type is available
 *
 * @param flowType - The flow type to check
 * @returns Visibility result with isEnabled flag and source
 *
 * @example
 * const { isEnabled } = useFlowTypeVisibility('elt');
 * if (isEnabled) {
 *   // Show ELT flow option
 * }
 */
export function useFlowTypeVisibility(flowType: FlowType): FlowTypeVisibilityResult {
  const currentOrg = useCurrentOrg();

  // Parse org features_enabled
  const orgFeaturesEnabled = useMemo(
    () => parseOrgFeaturesEnabled(currentOrg?.features_enabled),
    [currentOrg?.features_enabled],
  );

  // Check if database limits should be enforced
  const checkDatabaseLimits = ORG_FEATURE_FLAG_SETTINGS?.check_limits === true;

  // Get list of flow types that bypass limits (even when check_limits is true)
  const bypassLimitsFor = useMemo(() => {
    const bypass = ORG_FEATURE_FLAG_SETTINGS?.bypass_limits_for;
    if (!bypass) return [];
    if (Array.isArray(bypass)) return bypass;
    if (typeof bypass === 'string') {
      try {
        return JSON.parse(bypass);
      } catch {
        return [];
      }
    }
    return [];
  }, [ORG_FEATURE_FLAG_SETTINGS?.bypass_limits_for]);

  // Get all GrowthBook flags upfront using hooks (must be called at top level)
  // Currently we know api_server uses UI_ENABLE_API_SERVER_FLOW
  const enableApiServerFlow = useFeatureFlag('UI_ENABLE_API_SERVER_FLOW');

  // Map of flag keys to their hook values (add more as needed)
  const growthBookFlags = useMemo(() => {
    const flags: Record<string, boolean> = {};
    flags['UI_ENABLE_API_SERVER_FLOW'] = enableApiServerFlow;
    // Add more flags here as they're added to GROWTHBOOK_ROLLOUT_FLOW_TYPES
    return flags;
  }, [enableApiServerFlow]);

  // GrowthBook flag checker - uses pre-fetched hook values
  const getGrowthBookFlag = useMemo(() => {
    return (flagKey: string): boolean => {
      // Use pre-fetched hook value if available
      if (flagKey in growthBookFlags) {
        return growthBookFlags[flagKey];
      }
      // Fallback to false for unknown flags
      console.warn(`Unknown GrowthBook flag key: ${flagKey}`);
      return false;
    };
  }, [growthBookFlags]);

  // Build config
  const config: FlowTypeVisibilityConfig = useMemo(
    () => ({
      checkDatabaseLimits,
      bypassLimitsFor,
      orgFeaturesEnabled,
      getGrowthBookFlag,
    }),
    [checkDatabaseLimits, bypassLimitsFor, orgFeaturesEnabled, getGrowthBookFlag],
  );

  return useMemo(() => getFlowTypeVisibility(flowType, config), [flowType, config]);
}

/**
 * Hook to check multiple flow types at once
 *
 * @param flowTypes - Array of flow types to check
 * @returns Map of flow type to visibility result
 *
 * @example
 * const visibility = useFlowTypesVisibility(['elt', 'custom', 'spark']);
 * if (visibility.elt.isEnabled) {
 *   // Show ELT option
 * }
 */
export function useFlowTypesVisibility(
  flowTypes: FlowType[],
): Record<FlowType, FlowTypeVisibilityResult> {
  const currentOrg = useCurrentOrg();

  // Parse org features_enabled
  const orgFeaturesEnabled = useMemo(
    () => parseOrgFeaturesEnabled(currentOrg?.features_enabled),
    [currentOrg?.features_enabled],
  );

  // Check if database limits should be enforced
  const checkDatabaseLimits = ORG_FEATURE_FLAG_SETTINGS?.check_limits === true;

  // Get list of flow types that bypass limits (even when check_limits is true)
  const bypassLimitsFor = useMemo(() => {
    const bypass = ORG_FEATURE_FLAG_SETTINGS?.bypass_limits_for;
    if (!bypass) return [];
    if (Array.isArray(bypass)) return bypass;
    if (typeof bypass === 'string') {
      try {
        return JSON.parse(bypass);
      } catch {
        return [];
      }
    }
    return [];
  }, [ORG_FEATURE_FLAG_SETTINGS?.bypass_limits_for]);

  // Get all GrowthBook flags upfront using hooks
  const enableApiServerFlow = useFeatureFlag('UI_ENABLE_API_SERVER_FLOW');

  // Map of flag keys to their hook values
  const growthBookFlags = useMemo(() => {
    const flags: Record<string, boolean> = {};
    flags['UI_ENABLE_API_SERVER_FLOW'] = enableApiServerFlow;
    return flags;
  }, [enableApiServerFlow]);

  // GrowthBook flag checker - uses pre-fetched hook values
  const getGrowthBookFlag = useMemo(() => {
    return (flagKey: string): boolean => {
      if (flagKey in growthBookFlags) {
        return growthBookFlags[flagKey];
      }
      console.warn(`Unknown GrowthBook flag key: ${flagKey}`);
      return false;
    };
  }, [growthBookFlags]);

  // Build config
  const config: FlowTypeVisibilityConfig = useMemo(
    () => ({
      checkDatabaseLimits,
      bypassLimitsFor,
      orgFeaturesEnabled,
      getGrowthBookFlag,
    }),
    [checkDatabaseLimits, bypassLimitsFor, orgFeaturesEnabled, getGrowthBookFlag],
  );

  return useMemo(() => getFlowTypesVisibility(flowTypes, config), [flowTypes, config]);
}

/**
 * Legacy compatibility hook - returns the old format expected by components
 * { [flowType]: boolean }
 *
 * @returns Object mapping flow types to enabled boolean
 */
export function useConditionallyEnabledFlowTypes(): Partial<Record<FlowType, boolean>> {
  const currentOrg = useCurrentOrg();

  // Parse org features_enabled
  const orgFeaturesEnabled = useMemo(
    () => parseOrgFeaturesEnabled(currentOrg?.features_enabled),
    [currentOrg?.features_enabled],
  );

  // Check if database limits should be enforced
  const checkDatabaseLimits = ORG_FEATURE_FLAG_SETTINGS?.check_limits === true;

  // Get list of flow types that bypass limits (even when check_limits is true)
  const bypassLimitsFor = useMemo(() => {
    const bypass = ORG_FEATURE_FLAG_SETTINGS?.bypass_limits_for;
    if (!bypass) return [];
    if (Array.isArray(bypass)) return bypass;
    if (typeof bypass === 'string') {
      try {
        return JSON.parse(bypass);
      } catch {
        return [];
      }
    }
    return [];
  }, [ORG_FEATURE_FLAG_SETTINGS?.bypass_limits_for]);

  // Get all GrowthBook flags upfront using hooks
  const enableApiServerFlow = useFeatureFlag('UI_ENABLE_API_SERVER_FLOW');

  // Map of flag keys to their hook values
  const growthBookFlags = useMemo(() => {
    const flags: Record<string, boolean> = {};
    flags['UI_ENABLE_API_SERVER_FLOW'] = enableApiServerFlow;
    return flags;
  }, [enableApiServerFlow]);

  // GrowthBook flag checker - uses pre-fetched hook values
  const getGrowthBookFlag = useMemo(() => {
    return (flagKey: string): boolean => {
      if (flagKey in growthBookFlags) {
        return growthBookFlags[flagKey];
      }
      console.warn(`Unknown GrowthBook flag key: ${flagKey}`);
      return false;
    };
  }, [growthBookFlags]);

  // Build config
  const config: FlowTypeVisibilityConfig = useMemo(
    () => ({
      checkDatabaseLimits,
      bypassLimitsFor,
      orgFeaturesEnabled,
      getGrowthBookFlag,
    }),
    [checkDatabaseLimits, bypassLimitsFor, orgFeaturesEnabled, getGrowthBookFlag],
  );

  return useMemo(() => getConditionallyEnabledFlowTypes(config), [config]);
}

/**
 * Helper hook to check if a flow type is enabled (simple boolean)
 *
 * @param flowType - The flow type to check
 * @returns boolean indicating if the flow type is enabled
 */
export function useIsFlowTypeEnabled(flowType: FlowType): boolean {
  const { isEnabled } = useFlowTypeVisibility(flowType);
  return isEnabled;
}
